import * as React from 'react'
import { Field, type FieldProps } from '@/features/f0/ui'
import { ProtoButton } from '@/features/d0/states'
import { ProtoDialog } from '@/features/d0/ProtoDialog'
import { toApiError } from '@/features/a0/api/errors'

/** Every Company tab gets the active workspace id; the host remounts the tab when it changes. */
export interface TabProps {
  workspaceId: string
}

/** A load or save that belongs to a workspace/session that is no longer active, or was aborted: write nothing. */
export function isIgnorable(error: unknown): boolean {
  const code = toApiError(error).code
  return code === 'stale_identity' || code === 'stale_response' || code === 'aborted'
}

export function errorText(error: unknown): string {
  return toApiError(error).detail
}

export const CONFLICT_MESSAGE = 'Someone else changed this since you opened it. Reload to see the latest version, then make your change again.'

// Field is typed loosely (HTMLAttributes<HTMLElement>); narrow it to the real control props once.
type TextFieldProps = Omit<FieldProps, 'onChange'> & React.InputHTMLAttributes<HTMLInputElement>
export function TextField(props: TextFieldProps) {
  return <Field {...props} />
}
type TextAreaFieldProps = Omit<FieldProps, 'onChange' | 'as'> & React.TextareaHTMLAttributes<HTMLTextAreaElement>
export function TextAreaField(props: TextAreaFieldProps) {
  return <Field as="textarea" {...props} />
}
type SelectFieldProps = Omit<FieldProps, 'onChange' | 'as'> & React.SelectHTMLAttributes<HTMLSelectElement>
export function SelectField(props: SelectFieldProps) {
  return <Field as="select" {...props} />
}

export interface ConfirmActionProps {
  /** The button that asks, e.g. "Delete". */
  label: string
  /** The question shown in place, e.g. "Delete “Sourdough”?". */
  question: string
  confirmLabel?: string
  onConfirm: () => Promise<void> | void
  disabled?: boolean
}

/** In-page two-step confirm (the artifact frame and tests cannot rely on window.confirm). */
export function ConfirmAction({ label, question, confirmLabel = 'Yes, delete', onConfirm, disabled }: ConfirmActionProps) {
  const [asking, setAsking] = React.useState(false)
  const [busy, setBusy] = React.useState(false)

  if (!asking) {
    return (
      <ProtoButton className="z-quiet" onClick={() => setAsking(true)} disabled={disabled}>
        {label}
      </ProtoButton>
    )
  }
  return (
    <span className="z-co__confirm" role="group" aria-label={question}>
      <span className="z-co__confirm-q">{question}</span>
      <ProtoButton
        className="z-primary"
        disabled={busy}
        onClick={async () => {
          setBusy(true)
          try {
            await onConfirm()
          } finally {
            setBusy(false)
            setAsking(false)
          }
        }}
      >
        {confirmLabel}
      </ProtoButton>
      <ProtoButton className="z-quiet" disabled={busy} onClick={() => setAsking(false)}>
        Cancel
      </ProtoButton>
    </span>
  )
}

/** ConfirmAction in the prototype's modal (`dialog.ui-confirm-dialog`), for deletes and disconnects in Settings. */
export function ConfirmDialogAction({ label, question, confirmLabel = 'Yes, delete', onConfirm, disabled, eyebrow, children }: ConfirmActionProps & { eyebrow: string; children?: React.ReactNode }) {
  const [asking, setAsking] = React.useState(false)
  const [busy, setBusy] = React.useState(false)
  return (
    <>
      <ProtoButton className="z-quiet" onClick={() => setAsking(true)} disabled={disabled}>
        {label}
      </ProtoButton>
      <ProtoDialog
        open={asking}
        onOpenChange={(next) => { if (!busy) setAsking(next) }}
        eyebrow={eyebrow}
        title={question}
        role="alertdialog"
        footer={(
          <>
            <ProtoButton className="z-quiet" disabled={busy} onClick={() => setAsking(false)}>Cancel</ProtoButton>
            <ProtoButton
              className="z-primary"
              disabled={busy}
              onClick={async () => {
                setBusy(true)
                try {
                  await onConfirm()
                } finally {
                  setBusy(false)
                  setAsking(false)
                }
              }}
            >
              {confirmLabel}
            </ProtoButton>
          </>
        )}
      >
        {children}
      </ProtoDialog>
    </>
  )
}

/** Short status line under a form: saved / error / conflict, announced politely. */
export function FormStatus({ saved, error, conflict, onReload }: { saved?: boolean; error?: string | null; conflict?: boolean; onReload?: () => void }) {
  if (conflict) {
    return (
      <div role="alert" className="z-co__alert">
        <p>{CONFLICT_MESSAGE}</p>
        {onReload ? <ProtoButton onClick={onReload}>Reload</ProtoButton> : null}
      </div>
    )
  }
  if (error) return <p role="alert" className="z-co__alert">{error}</p>
  if (saved) return <p role="status" className="z-co__saved">Saved.</p>
  return null
}

/** A number the API does not know yet renders as words, never as 0. */
export function knownNumber(value: number | null | undefined, format: (n: number) => string = String): string {
  return value === null || value === undefined ? 'Not known yet' : format(value)
}
